import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../layouts/Header';
import { Card, CardBody, CardTitle, Row, Col } from 'reactstrap';
import { Button, Modal } from 'react-bootstrap';
import { getAllContacts, deleteContact } from '../../helper/contact_helper';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import DataTableSkeleton from '../../components/DataTableSkeleton';
import ConfirmModal from '../../components/ConfirmModal';
import Footer from '../../layouts/Footer';
import Loader from '../../layouts/Loader';
import moment from 'moment';
import AdminRowActions, { AdminActionButton } from '../../components/admin/AdminRowActions';
import AdminEmptyState from '../../components/admin/AdminEmptyState';

const CONTACT_READ_KEY = 'careveli_contact_read';

function getContactMessage(contact) {
    return (contact?.msg ?? contact?.message ?? '').trim();
}

function loadReadIds() {
    try {
        return new Set(JSON.parse(localStorage.getItem(CONTACT_READ_KEY) || '[]'));
    } catch {
        return new Set();
    }
}

function Contact() {
    const user = useSelector(state => state.user);
    const [loading, setLoading] = useState(false);
    const [contacts, setContacts] = useState([]);
    const [filteredContacts, setFilteredContacts] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [currentContact, setCurrentContact] = useState(null);
    const [confirm, setConfirm] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [readIds, setReadIds] = useState(loadReadIds);
    const [viewContact, setViewContact] = useState(null);

    const markAsRead = useCallback((contactId) => {
        setReadIds(prev => {
            if (prev.has(contactId)) return prev;
            const next = new Set(prev);
            next.add(contactId);
            localStorage.setItem(CONTACT_READ_KEY, JSON.stringify([...next]));
            return next;
        });
    }, []);

    const openMessagePreview = useCallback((contact) => {
        setViewContact(contact);
        if (contact?._id) markAsRead(contact._id);
    }, [markAsRead]);

    const toggleReadStatus = useCallback((contactId) => {
        setReadIds(prev => {
            const next = new Set(prev);
            if (next.has(contactId)) next.delete(contactId);
            else next.add(contactId);
            localStorage.setItem(CONTACT_READ_KEY, JSON.stringify([...next]));
            return next;
        });
    }, []);

    const handleDeleteClick = useCallback((contact) => {
        setCurrentContact(contact);
        setConfirm(true);
    }, []);

    const columns = useMemo(() => [
        {
            name: 'Date',
            selector: row => moment(row.createdAt).format('DD MMM YYYY, hh:mm A'),
            sortable: true,
            width: '170px',
        },
        {
            name: 'Name',
            selector: row => row.name,
            sortable: true,
            width: '140px',
        },
        {
            name: 'Email',
            selector: row => row.email,
            sortable: true,
            width: '200px',
            cell: row => (
                <span className="text-truncate d-inline-block" style={{ maxWidth: '190px' }} title={row.email}>
                    {row.email}
                </span>
            ),
        },
        {
            name: 'Read',
            width: '100px',
            cell: row => {
                const isRead = readIds.has(row._id);
                return (
                    <span className={`badge rounded-pill ${isRead ? 'bg-secondary' : 'bg-primary'}`}>
                        {isRead ? 'Read' : 'Unread'}
                    </span>
                );
            },
        },
        {
            name: 'Message',
            selector: row => row.msg,
            grow: 2,
            cell: row => {
                const text = getContactMessage(row);
                return (
                    <button
                        type="button"
                        className="btn btn-link text-start text-truncate p-0 border-0 text-body"
                        style={{ maxWidth: '280px' }}
                        title="Click to read full message"
                        onClick={() => openMessagePreview(row)}
                    >
                        {text || '—'}
                    </button>
                );
            },
        },
        {
            name: 'Actions',
            cell: row => (
                <AdminRowActions>
                    <AdminActionButton
                        action="reply"
                        label="Reply via email"
                        onClick={() => {
                            toggleReadStatus(row._id);
                            window.open(
                                `mailto:${row.email}?subject=${encodeURIComponent('Re: Your message to Careveli')}`,
                                '_blank'
                            );
                        }}
                    >
                        <span className="d-none d-lg-inline">Reply</span>
                    </AdminActionButton>
                    <AdminActionButton action="view" label="View full message" onClick={() => openMessagePreview(row)} />
                    <AdminActionButton action="delete" label="Delete message" onClick={() => handleDeleteClick(row)} />
                </AdminRowActions>
            ),
            ignoreRowClick: true,
            minWidth: '200px',
        },
    ], [readIds, toggleReadStatus, handleDeleteClick, openMessagePreview]);

    // Fetch all contacts on component mount
    useEffect(() => {
        getData();
    }, []);

    // Filter contacts based on search text
    useEffect(() => {
        if (searchText === '') {
            setFilteredContacts(contacts);
        } else {
            const filtered = contacts.filter(contact =>
                contact.name.toLowerCase().includes(searchText.toLowerCase()) ||
                contact.email.toLowerCase().includes(searchText.toLowerCase()) ||
                getContactMessage(contact).toLowerCase().includes(searchText.toLowerCase())
            );
            setFilteredContacts(filtered);
        }
    }, [searchText, contacts]);

    // Get all contacts from API
    const getData = async () => {
        setLoading(true);
        try {
            const response = await getAllContacts();
            console.log('Contact response:', response);

            if (response.success) {
                setContacts(response.contacts || []);
                setFilteredContacts(response.contacts || []);
            } else {
                toast.error(response.message || 'Failed to fetch contacts');
                setContacts([]);
                setFilteredContacts([]);
            }
        } catch (error) {
            console.error('Error fetching contacts:', error);
            toast.error('Failed to load contact messages');
            setContacts([]);
            setFilteredContacts([]);
        } finally {
            setLoading(false);
        }
    };

    // Delete contact
    const handleDelete = async () => {
        if (!currentContact?._id) {
            toast.error('Invalid contact ID');
            return;
        }

        setLoading(true);
        try {
            const response = await deleteContact(currentContact._id);
            console.log('Delete response:', response);

            if (response.success) {
                toast.success(response.message || 'Contact deleted successfully');
                // Remove from local state
                const updatedContacts = contacts.filter(c => c._id !== currentContact._id);
                setContacts(updatedContacts);
                setFilteredContacts(updatedContacts);
            } else {
                toast.error(response.message || 'Failed to delete contact');
            }
        } catch (error) {
            console.error('Error deleting contact:', error);
            toast.error('Failed to delete contact');
        } finally {
            setLoading(false);
            setConfirm(false);
            setCurrentContact(null);
        }
    };

    // Handle bulk delete
    const handleBulkDelete = async () => {
        if (selectedRows.length === 0) {
            toast.warning('No contacts selected');
            return;
        }

        if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} contact(s)?`)) {
            return;
        }

        setLoading(true);
        let successCount = 0;
        let failCount = 0;

        for (const contact of selectedRows) {
            try {
                const response = await deleteContact(contact._id);
                if (response.success) {
                    successCount++;
                } else {
                    failCount++;
                }
            } catch (error) {
                console.error('Error deleting contact:', error);
                failCount++;
            }
        }

        if (successCount > 0) {
            toast.success(`Successfully deleted ${successCount} contact(s)`);
            getData(); // Refresh list
        }
        if (failCount > 0) {
            toast.error(`Failed to delete ${failCount} contact(s)`);
        }

        setLoading(false);
        setSelectedRows([]);
        setToggleCleared(!toggleCleared);
    };

    // Handle row selection
    const handleRowSelected = React.useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    // Custom styles for DataTable
    const customStyles = {
        rows: {
            style: {
                minHeight: '60px',
            },
        },
        headCells: {
            style: {
                fontSize: '14px',
                fontWeight: 'bold',
                paddingLeft: '8px',
                paddingRight: '8px',
            },
        },
        cells: {
            style: {
                paddingLeft: '8px',
                paddingRight: '8px',
            },
        },
    };

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="d-md-flex align-items-center justify-content-between mb-4">
                    <div>
                        <ol className="breadcrumb fs-sm mb-1">
                            <li className="breadcrumb-item"><Link to="/admin/dashboard">Dashboard</Link></li>
                            <li className="breadcrumb-item active" aria-current="page">Contact Messages</li>
                        </ol>
                        <h4 className="main-title mb-0 admin-page-title">Contact Messages</h4>
                        <p className="text-secondary small mb-0 mt-1">Manage customer contact messages and inquiries</p>
                    </div>
                </div>

                <Row>
                    <Col xl={12}>
                        <Card>
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <CardTitle tag="h5" className="mb-0">
                                        All Messages ({filteredContacts.length})
                                    </CardTitle>
                                    <div className="d-flex gap-2">
                                        {selectedRows.length > 0 && (
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={handleBulkDelete}
                                            >
                                                <i className="fa fa-trash me-1"></i>
                                                Delete Selected ({selectedRows.length})
                                            </Button>
                                        )}
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={getData}
                                        >
                                            <i className="fa fa-refresh me-1"></i>
                                            Refresh
                                        </Button>
                                    </div>
                                </div>

                                {/* Search Filter */}
                                <div className="mb-3">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by name, email, or message..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        style={{ maxWidth: '400px' }}
                                    />
                                </div>

                                {/* Data Table */}
                                <DataTable
                        progressPending={loading}
                        progressComponent={<DataTableSkeleton />}
                                    columns={columns}
                                    data={filteredContacts}
                                    pagination
                                    paginationPerPage={20}
                                    paginationRowsPerPageOptions={[10, 20, 50, 100]}
                                    highlightOnHover
                                    striped
                                    responsive
                                    selectableRows
                                    onRowClicked={openMessagePreview}
                                    pointerOnHover
                                    onSelectedRowsChange={handleRowSelected}
                                    clearSelectedRows={toggleCleared}
                                    customStyles={customStyles}
                                    noDataComponent={
                                        <AdminEmptyState
                                            icon="ri-mail-line"
                                            title="No contact messages"
                                            description="Messages sent from the store's contact form will appear here."
                                        />
                                    }
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Modal
                    show={Boolean(viewContact)}
                    onHide={() => setViewContact(null)}
                    centered
                    size="lg"
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Message from {viewContact?.name || 'Customer'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {viewContact && (
                            <>
                                <div className="mb-3 small text-muted">
                                    <div>
                                        <strong>Date:</strong>{' '}
                                        {moment(viewContact.createdAt).format('DD MMM YYYY, hh:mm A')}
                                    </div>
                                    <div>
                                        <strong>Email:</strong>{' '}
                                        <a href={`mailto:${viewContact.email}`}>{viewContact.email}</a>
                                    </div>
                                    <div className="mt-1">
                                        <strong>Status:</strong>{' '}
                                        <span className={`badge rounded-pill ${readIds.has(viewContact._id) ? 'bg-secondary' : 'bg-primary'}`}>
                                            {readIds.has(viewContact._id) ? 'Read' : 'Unread'}
                                        </span>
                                    </div>
                                </div>
                                <div
                                    className="border rounded p-3 bg-light"
                                    style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '50vh', overflowY: 'auto' }}
                                >
                                    {getContactMessage(viewContact) || (
                                        <span className="text-muted">No message content</span>
                                    )}
                                </div>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="d-flex flex-wrap gap-2 justify-content-between">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => viewContact?._id && toggleReadStatus(viewContact._id)}
                        >
                            {viewContact && readIds.has(viewContact._id) ? 'Mark as unread' : 'Mark as read'}
                        </Button>
                        <div className="d-flex gap-2">
                            {viewContact?.email && (
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => {
                                        const body = encodeURIComponent(
                                            `\n\n---\nOriginal message:\n${getContactMessage(viewContact)}`
                                        );
                                        window.open(
                                            `mailto:${viewContact.email}?subject=${encodeURIComponent('Re: Your message to Careveli')}&body=${body}`,
                                            '_blank'
                                        );
                                    }}
                                >
                                    <i className="ri-reply-line me-1" />
                                    Reply via email
                                </Button>
                            )}
                            <Button variant="outline-secondary" onClick={() => setViewContact(null)}>
                                Close
                            </Button>
                        </div>
                    </Modal.Footer>
                </Modal>

                {/* Confirm Delete Modal */}
                <ConfirmModal
                    show={confirm}
                    onCloseClick={() => {
                        setConfirm(false);
                        setCurrentContact(null);
                    }}
                    onConfirm={handleDelete}
                    data={{
                        actionType: 'Delete'
                    }}
                />

                {/* Loading Overlay */}
                <Footer />
            </div>
        </React.Fragment>
    );
}

export default Contact;
